import React from "react"
import { areaY, defineChart, lineY } from "@tanstack/charts"
import { motion } from "@tanstack/charts/motion"
import { Chart } from "@tanstack/charts/react/core"
import { scaleLinear } from "@tanstack/charts/scales/linear"
import styled, { useTheme } from "styled-components"
import { NO_SIGNAL_STRENGTH, SAMPLE_INTERVAL_MS, SignalStrengthPoint } from "../../../hooks/useSignalStrengthHistory"
import H3 from "../../atoms/typography/H3"
import { HorizontalSpacer } from "../../atoms/spacers"

const ChartContainer = styled.div`
  min-width: 0;
  width: 100%;
  font-family: "Inter", "Inter-fallback", sans-serif;
`

const timestampFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
})

const formatTimestamp = (timestamp: number) => timestampFormatter.format(timestamp)

const chartRenderer = motion<SignalStrengthPoint, number, number>()
const chartMotion = {
  // The retained window is translated as one path. "snap" keeps a failed
  // invariant obvious instead of silently morphing every vertex of the curve.
  path: { update: "rolling" as const, x: "shift" as const, y: "reproject" as const, fallback: "snap" as const },
  // Matching the sample interval makes the trace scroll continuously rather
  // than jump and then wait for the next sample.
  transition: { type: "tween" as const, duration: SAMPLE_INTERVAL_MS, easing: "linear" as const },
}

// The no-signal reading is the floor of the chart and the baseline of the area
// beneath the line.
const SIGNAL_STRENGTH_FLOOR = NO_SIGNAL_STRENGTH

// The upper bound follows the retained samples but is rounded up to a whole
// step, so the domain, and with it the tick labels, only changes when the
// signal crosses a step rather than on every sample. The rolling motion
// reprojects the retained path when the domain does change.
const SIGNAL_STRENGTH_STEP = 5
const MIN_SIGNAL_STRENGTH_CEILING = 0

const signalStrengthDomain = (points: SignalStrengthPoint[]): [number, number] => {
  let min = SIGNAL_STRENGTH_FLOOR
  let max = MIN_SIGNAL_STRENGTH_CEILING
  for (const { signalStrength } of points) {
    if (signalStrength < min) min = signalStrength
    if (signalStrength > max) max = signalStrength
  }
  return [
    Math.floor(min / SIGNAL_STRENGTH_STEP) * SIGNAL_STRENGTH_STEP,
    Math.ceil(max / SIGNAL_STRENGTH_STEP) * SIGNAL_STRENGTH_STEP,
  ]
}

/**
 * Automatic margins grow to fit the outermost tick label, so a timestamp
 * sliding towards the edge of the plot would resize the plot area and break the
 * rolling shift for that frame. Locking every side keeps the plot bounds
 * constant. Each value covers the widest label it has to fit at the tick size,
 * tick padding and guide inset the axes use: "100 dB" beside the y axis, and
 * half of a "00:00:00" label centred on the last x tick.
 */
const chartMargin = { top: 12, right: 30, bottom: 26, left: 48 }

const yAxisOptions = {
  ticks: { format: (value: number) => `${value} dB` },
  tickLabels: { fontSize: 11, fontWeight: 500, opacity: 1 },
}

const SignalStrengthLineChart: React.FunctionComponent<{
  points: SignalStrengthPoint[]
  timeDomain?: readonly [number, number]
}> = ({ points, timeDomain }) => {
  const activeTheme = useTheme()
  const [yMin, yMax] = signalStrengthDomain(points)
  const definition = React.useMemo(
    () =>
      defineChart({
        clip: true,
        margin: chartMargin,
        motion: chartMotion,
        marks: [
          areaY(points, {
            x: "timestamp",
            y: "signalStrength",
            // A constant baseline lets the rolling motion reproject both edges.
            y1: SIGNAL_STRENGTH_FLOOR,
            key: "timestamp",
            fill: "url(#blue-gradient)",
            fillOpacity: 1,
            stroke: "transparent",
          }),
          lineY(points, {
            x: "timestamp",
            y: "signalStrength",
            key: "timestamp",
            stroke: activeTheme.gradients.blueBorder,
            strokeWidth: 2.5,
          }),
        ],
        x: {
          // A scale instance keeps its configured domain; a bare factory would
          // have its domain re-inferred from the sample extent, which leaves no
          // overscan outside the plot for the rolling shift to come from.
          scale: timeDomain ? scaleLinear().domain(timeDomain) : scaleLinear,
          axis: {
            ticks: { format: formatTimestamp },
            tickLabels: { fontSize: 11, fontWeight: 500, opacity: 1 },
          },
        },
        y: {
          scale: scaleLinear().domain([yMin, yMax]),
          grid: true,
          axis: yAxisOptions,
        },
        gradients: [
          {
            id: "blue-gradient",
            stops: [
              { offset: 0, color: activeTheme.colors.blue, opacity: 0 },
              { offset: 1, color: activeTheme.gradients.blueBorder, opacity: 0.4 },
            ],
          },
        ],
        theme: {
          foreground: activeTheme.colors.greyDarker,
          muted: activeTheme.colors.greyDark,
          grid: activeTheme.colors.greyLight,
          background: "transparent",
        },
      }),
    [activeTheme, points, timeDomain, yMin, yMax],
  )

  return (
    <ChartContainer>
      <H3>Signal strength over time</H3>
      <HorizontalSpacer spacing={1} />
      <Chart
        definition={definition}
        renderer={chartRenderer}
        height={220}
        initialWidth={320}
        ariaLabel="Signal strength over time"
      />
    </ChartContainer>
  )
}

export default React.memo(SignalStrengthLineChart)
